import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { Observable } from 'rxjs';
import { take, map, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { User } from '@models/user';

/**
 * Guard that prevents Dining Manager users from accessing non-Menus routes.
 * Dining Managers (role id 8) may only access the Menus tab within their
 * assigned properties. Attempting to navigate to any other content route
 * redirects them to the Menus page for the site in the URL (if they are
 * associated with it), or their default site as a fallback.
 */
@Injectable({
  providedIn: 'root',
})
export class DiningManagerGuard {
  constructor(
    private auth: AuthService,
    private router: Router,
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.auth.userReplay.pipe(
      take(1),
      map((user: User) => {
        if (!user?.isDiningManager) {
          // Not a Dining Manager — allow navigation
          return { allowed: true, user };
        }
        // Dining Manager: only the menus route is allowed
        const url = state.url;
        if (url.includes('/menus')) {
          return { allowed: true, user };
        }
        return { allowed: false, user };
      }),
      tap(({ allowed, user }) => {
        if (!allowed) {
          // Prefer the siteId already in the URL if it belongs to the user,
          // so that a multi-site DM navigating to /manage/content/20/announcements
          // is sent to /manage/content/20/menus rather than their default site.
          const urlMatch = state.url.match(/\/manage\/content\/(\d+)\//);
          const urlSiteId = urlMatch ? Number(urlMatch[1]) : null;
          const siteId =
            urlSiteId && user?.associatedWithSite(urlSiteId)
              ? urlSiteId
              : user?.defaultSiteId;
          if (siteId) {
            this.router.navigate([`/manage/content/${siteId}/menus`]);
          } else {
            this.router.navigate(['/session/sign-in']);
          }
        }
      }),
      map(({ allowed }) => allowed),
    );
  }
}
